import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { ProductFormUtils } from '../../utils/productCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import {
  getProductOptionGroupList,
  type ProductOptionGroupListItem,
} from '../../utils/productOptionGroup';
import { formatDateFromApi } from '../../utils/dateTime';

import {
  Home,
  Search,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Layers,
} from 'lucide-react';

function formatSelectionRule(item: ProductOptionGroupListItem): string {
  if (item.selectionRule) {
    const map: Record<string, string> = {
      REQUIRED_SINGLE: '필수 · 단일',
      REQUIRED_MULTI: '필수 · 다중',
      OPTIONAL_SINGLE: '선택 · 단일',
      OPTIONAL_MULTI: '선택 · 다중',
      SINGLE: '단일',
      MULTI: '다중',
    };
    return map[item.selectionRule] || item.selectionRule;
  }
  const req =
    item.required === true ? '필수' : item.required === false ? '선택' : '';
  const single =
    item.singleChoice === true ? '단일' : item.singleChoice === false ? '다중' : '';
  if (req && single) return `${req} · ${single}`;
  if (req) return req;
  if (single) return single;
  return '—';
}

const ProductOptionGroupList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-option-group-list');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  const [groups, setGroups] = useState<ProductOptionGroupListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const projectInfo = ProductFormUtils.loadProjectDetail();
    if (projectInfo) setProjectDetail(projectInfo);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    if (page !== null) setCurrentPage(parseInt(page, 10));
    if (size !== null) setPageSize(parseInt(size, 10));
  }, [location.search]);

  useEffect(() => {
    if (!projectDetail) return;
    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;
    let cancelled = false;
    getBrandListFlat(projectCode)
      .then((res) => {
        if (!cancelled && res?.brands) setBrands(res.brands);
      })
      .catch((e) => {
        if (!cancelled) console.error('브랜드 목록 조회 오류:', e);
      });
    return () => {
      cancelled = true;
    };
  }, [projectDetail?.code]);

  useEffect(() => {
    const t = searchTerm.trim();
    if (t.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchTerm(t), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const run = async () => {
      const projectCode = projectDetail?.code || getProjectCode();
      if (!projectCode) return;

      setIsLoading(true);
      setError(null);
      try {
        const res = await getProductOptionGroupList(projectCode, {
          page: currentPage,
          size: pageSize,
          sort: `${sortField},${sortOrder}`,
          keyword: debouncedSearchTerm || undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          brandCode: selectedBrand !== 'all' ? selectedBrand : undefined,
        });
        setGroups(res.groups);
        setTotalCount(res.totalCount);
        if (res.pageSize && res.pageSize !== pageSize) {
          setPageSize((prev) => (prev !== res.pageSize ? res.pageSize! : prev));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
        setGroups([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [
    projectDetail?.code,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    debouncedSearchTerm,
    selectedStatus,
    selectedBrand,
  ]);

  const getStatusBadge = (status: string) => {
    const badgeInfo = ProductFormUtils.getStatusBadge(status);
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeInfo.color}`}
      >
        <span className="w-1.5 h-1.5 mr-1 bg-current rounded-full" />
        {badgeInfo.text}
      </span>
    );
  };

  const formatDate = (s: string) => (s ? formatDateFromApi(s) || s.split(' ')[0] : '—');

  const brandLabel = (code: string) =>
    code ? brands.find((b) => b.code === code)?.name ?? code : '—';

  const handleSort = (field: string) => {
    ProductFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  const handleSelectAll = () => {
    CommonUtils.handleSelectAll(selectedCodes, groups, setSelectedCodes, (g) => g.code);
  };

  const handleSelectOne = (code: string) => {
    CommonUtils.handleSelectItem(code, selectedCodes, setSelectedCodes);
  };

  const handlePageChange = (page: number) => {
    ProductFormUtils.handlePageChange(page, setCurrentPage);
  };

  const handlePageSizeChange = (size: number) => {
    ProductFormUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  };

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          {error ? (
            <>
              <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                새로고침
              </button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
              <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="상품 옵션그룹 목록"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <div className="relative flex-1 sm:max-w-xs min-w-[150px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="키워드 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="PENDING">대기중</option>
                    <option value="ARCHIVED">보관됨</option>
                  </select>
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 브랜드</option>
                    {brands.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/products/option-groups/create')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    옵션그룹 추가
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{totalCount}</span>개 /{' '}
                  <span className="ml-1">
                    검색결과 <span className="font-medium text-gray-900">{groups.length}</span>개
                  </span>
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1" />
                    활성: {groups.filter((g) => g.status === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1" />
                    비활성: {groups.filter((g) => g.status === 'INACTIVE').length}
                  </span>
                </div>
              </div>
            </div>

            {error && !isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  다시 시도
                </button>
              </div>
            )}

            {!error && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200" style={{ width: '100%', minWidth: '1120px' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="pl-4 pr-0 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={
                              selectedCodes.length === groups.length && groups.length > 0
                            }
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="pl-4 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>
                          브랜드
                        </th>
                        <th className="pl-2 pr-2 py-3 text-left" style={{ minWidth: '140px' }}>
                          <button
                            type="button"
                            onClick={() => handleSort('code')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            그룹코드
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-2 pr-2 py-3 text-left" style={{ minWidth: '180px' }}>
                          <button
                            type="button"
                            onClick={() => handleSort('name')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            그룹명
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-2 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '110px' }}>
                          상태
                        </th>
                        <th className="pl-2 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '130px' }}>
                          선택규칙
                        </th>
                        <th className="pl-2 pr-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '90px' }}>
                          항목 수
                        </th>
                        <th className="pl-2 pr-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '110px' }}>
                          매핑 상품 수
                        </th>
                        <th className="pl-2 pr-2 py-3 text-left hidden lg:table-cell" style={{ minWidth: '120px' }}>
                          <button
                            type="button"
                            onClick={() => handleSort('createdAt')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            등록일
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="relative px-4 py-3 text-right">
                          <span className="sr-only">작업</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={10} className="px-6 py-12 text-center">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                            </div>
                          </td>
                        </tr>
                      ) : groups.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <Layers className="w-8 h-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">등록된 옵션 그룹이 없습니다.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        groups.map((g) => (
                          <tr key={g.code} className="hover:bg-gray-50">
                            <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedCodes.includes(g.code)}
                                onChange={() => handleSelectOne(g.code)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="pl-4 pr-2 py-4 text-sm text-gray-900 whitespace-nowrap" style={{ minWidth: '140px' }}>
                              {g.brandName || brandLabel(g.brandCode)}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm font-medium text-gray-900 whitespace-nowrap" style={{ minWidth: '140px' }}>
                              {g.code}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm text-gray-900 max-w-[200px] truncate" style={{ minWidth: '180px' }}>
                              {g.name || '—'}
                            </td>
                            <td className="pl-2 pr-2 py-4 whitespace-nowrap" style={{ minWidth: '110px' }}>
                              {getStatusBadge(g.status)}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm text-gray-700 whitespace-nowrap" style={{ minWidth: '130px' }}>
                              {formatSelectionRule(g)}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm text-gray-900 text-right whitespace-nowrap" style={{ minWidth: '90px' }}>
                              {g.itemProductCount}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm text-gray-900 text-right whitespace-nowrap" style={{ minWidth: '110px' }}>
                              {g.mappingProductCount}
                            </td>
                            <td className="pl-2 pr-2 py-4 text-sm text-gray-500 whitespace-nowrap hidden lg:table-cell" style={{ minWidth: '120px' }}>
                              {formatDate(g.createdAt)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-gray-600"
                                  title="상세"
                                  onClick={() => alert('상세 보기는 준비중입니다.')}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-blue-600"
                                  title="수정"
                                  onClick={() =>
                                    navigate(`/products/option-groups/${encodeURIComponent(g.code)}/update`)
                                  }
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-600"
                                  title="삭제"
                                  onClick={() => alert('삭제는 준비중입니다.')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {selectedCodes.length > 0 && (
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-sm text-gray-700">
                        {selectedCodes.length}개 선택됨
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          상태 변경
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          엑셀 다운로드
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          선택 삭제
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!error && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">페이지당</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                      className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10개</option>
                      <option value={20}>20개</option>
                      <option value={50}>50개</option>
                      <option value={100}>100개</option>
                    </select>
                    <span>표시</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handlePageChange(0)}
                      disabled={currentPage === 0}
                      className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      처음
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i)
                      .slice(0, 5)
                      .map((page) => (
                        <button
                          key={page}
                          type="button"
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 rounded text-sm ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {page + 1}
                        </button>
                      ))}
                    <button
                      type="button"
                      onClick={() =>
                        handlePageChange(Math.min(totalPages - 1, currentPage + 1))
                      }
                      disabled={currentPage >= totalPages - 1}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(totalPages - 1)}
                      disabled={currentPage >= totalPages - 1}
                      className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      마지막
                    </button>
                  </div>

                  <p className="text-sm text-gray-600">
                    {totalCount === 0
                      ? '0 / 총 0개'
                      : `${currentPage * pageSize + 1} - ${Math.min(
                          (currentPage + 1) * pageSize,
                          totalCount
                        )} / 총 ${totalCount}개`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductOptionGroupList;